import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { H1, Sub } from "./_shared";

export default function PaymentsSection() {
  const [items, setItems] = useState([]);
  const [refunding, setRefunding] = useState(null);
  const load = useCallback(() => api.get("/admin/payments").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const doRefund = async (payment, amount) => {
    try {
      const { data } = await api.post(`/admin/payments/${payment.id}/refund`,
        amount && amount < payment.amount ? { amount } : {});
      if (data.error) toast.warning(`Recorded but Stripe call failed: ${data.error}`);
      else if (data.partial) toast.success(`Partially refunded ${data.amount} via Stripe`);
      else toast.success("Refunded via Stripe");
      setRefunding(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  return (
    <div data-testid="cp-payments">
      <H1>Payments</H1>
      <Sub>Real Stripe refunds — supports partial amounts. Email receipts auto-sent.</Sub>
      <div className="mt-6 pp-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-white/5"><tr>
            <th className="text-start px-4 py-3">Provider ID</th>
            <th className="text-start px-4 py-3">Amount</th>
            <th className="text-start px-4 py-3">Fee</th>
            <th className="text-start px-4 py-3">Status</th>
            <th className="text-start px-4 py-3">Created</th>
            <th className="text-end px-4 py-3">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-white/5" data-testid={`cp-payment-row-${p.id}`}>
                <td className="px-4 py-2 font-mono text-xs">{(p.provider_payment_id || "").slice(0, 22)}</td>
                <td className="px-4 py-2 font-bold">{p.amount} {p.currency?.toUpperCase()}</td>
                <td className="px-4 py-2 text-zinc-400">{p.platform_fee || 0}</td>
                <td className="px-4 py-2"><span className={`pp-chip ${p.status === "paid" ? "pp-chip-active" : ""}`}>{p.status}{p.refunded_amount ? ` · ${p.refunded_amount}` : ""}</span></td>
                <td className="px-4 py-2 text-zinc-500 text-xs">{new Date(p.created_at).toLocaleString()}</td>
                <td className="px-4 py-2 text-end">
                  {(p.status === "paid" || p.status === "partially_refunded") && <button onClick={() => setRefunding(p)} data-testid={`cp-payment-refund-${p.id}`} className="pp-btn-secondary px-3 py-1 rounded-lg text-xs">Refund</button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 py-10">No payments yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {refunding && <RefundDialog payment={refunding} onClose={() => setRefunding(null)} onConfirm={doRefund} />}
    </div>
  );
}

function RefundDialog({ payment, onClose, onConfirm }) {
  const [amount, setAmount] = useState(payment.amount);
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#131313] border-white/10 text-white max-w-md" data-testid="cp-refund-dialog">
        <DialogHeader><DialogTitle>Refund payment</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400">Original amount: <strong className="text-white">{payment.amount} {payment.currency?.toUpperCase()}</strong></p>
        <label className="block">
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Refund amount</div>
          <input type="number" step={0.01} min={0.01} max={payment.amount} value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            data-testid="cp-refund-amount" className="pp-input" />
        </label>
        <p className="text-xs text-zinc-500">Calls <code>stripe.Refund.create</code>. The payer receives an email receipt.</p>
        <DialogFooter>
          <button onClick={onClose} className="pp-btn-secondary px-4 py-2 rounded-lg">Cancel</button>
          <button onClick={() => onConfirm(payment, amount)} data-testid="cp-refund-confirm" className="pp-btn-primary px-4 py-2 rounded-lg">Refund {amount} {payment.currency?.toUpperCase()}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
